import React, { FC, useCallback, useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import classNames from 'classnames';
import { debounce } from 'lodash';

import type { ChangeInputHandler } from '../../../../types';
import type { CurrencyCode } from 'app/api/services/site-configs/types';
import { FeatureFlag } from 'app/api/services/site-configs/types';
import type { ReduxState } from 'app/redux/types';

import CurrencySymbol from 'app/design-system/components/CurrencySymbol';
import { useFeatureFlag } from 'app/lib/feature-flags';
import { getDecimalsConfig } from 'app/lib/site-configs/selectors';
import selectCurrency from 'app/redux/selectors/сurrency';
import AmountInputComponent, { OnChangeHandler } from 'app/widgets/InputsWithAnalytics/AmountInput';

import { AccountType, ActionButtonState, SubmissionState } from '../../../../constants';
import {
  selectAnyHasLockedState,
  selectBets,
  selectSomeHasError,
  selectTotalAmountActive,
} from '../../../../selectors/entries';
import {
  selectIsOnlyAcceptAmountSingleError,
  selectOnlyAcceptAmountExpressError,
  selectSingleBetsInfo,
} from '../../../../selectors/stakeTypes/single';
import { selectSubmission } from '../../../../selectors/submission';
import actionButtonState from '../../../../utils/action-button-state';

import styles from './AmountInput.module.scss';

// ---

type StoreProps = {
  required: boolean;
  currency?: CurrencyCode;
  disabled: boolean;
  price?: number | null;
  totalAmountActive: boolean;
  isOnlyAcceptSingleError: boolean;
  singleRejectAmount?: number | null;
  isOnlyAcceptExpressError: boolean;
  expressRejectAmount?: number | null;
};

type InputProps = {
  betId?: number;
  name?: string;
  account?: AccountType;
  placeholder?: string;
  value: string;
  inputClassName?: string;
  unitsClassName?: string;
  disableDebounce?: boolean;
  onChange: ChangeInputHandler;
  onFocus?: () => void;
  onBlur?: () => void;
  'data-test'?: string;
};

const DEBOUNCE_TIMEOUT = 300;
const onChangeWithDebounce = debounce((value, onChange) => onChange(value), DEBOUNCE_TIMEOUT);

const AmountInput: FC<InputProps & StoreProps> = ({
  betId,
  price,
  totalAmountActive,
  value,
  name,
  account,
  placeholder,
  inputClassName,
  unitsClassName,
  disableDebounce = false,
  onChange,
  onFocus,
  onBlur,
  required,
  currency,
  disabled,
  isOnlyAcceptSingleError,
  singleRejectAmount,
  isOnlyAcceptExpressError,
  expressRejectAmount,
  ...props
}) => {
  const isAllowChain = useFeatureFlag(FeatureFlag.CHAIN);
  const [inputValue, onChangeInput] = useState(value);
  const [isFocus, setIsFocus] = useState(false);
  const decimalsConfig = useSelector(getDecimalsConfig);

  const changeInputHandler: OnChangeHandler = useCallback((newValue) => {
    if (newValue !== inputValue) {
      onChangeInput(newValue);

      if (disableDebounce) {
        onChange({ value: newValue, name, account });
      }
      else {
        onChangeWithDebounce({ value: newValue, name, account }, onChange);
      }
    }
  }, [inputValue, onChange]);

  const focusInputHandler = useCallback(() => {
    setIsFocus(true);
    onFocus && onFocus();
  }, [onFocus]);

  const blurInputHandler = useCallback(() => {
    setIsFocus(false);
    onBlur && onBlur();
  }, [onBlur]);

  useEffect(() => {
    onChangeInput(value);
  }, [value]);

  const _className = classNames(styles.input, {
    [styles.focus]: isFocus,
    [styles.disabled]: disabled,
  }, inputClassName);

  const dataGaAttributes = {
    ['data-ga-event']: totalAmountActive ? 'cart-bidUnitedAmountInput' : 'cart-bidAmountInput',
    ['data-ga-event-content']: betId,
    ['data-ga-event-value']: price,
  };

  useEffect(() => {
    if (isOnlyAcceptSingleError && singleRejectAmount) {
      onChange({ value: `${singleRejectAmount}`, name, account });
    }

    if (isOnlyAcceptExpressError && expressRejectAmount) {
      onChange({ value: `${expressRejectAmount}`, name, account });
    }
  }, [
    singleRejectAmount,
    expressRejectAmount,
    isOnlyAcceptSingleError,
    isOnlyAcceptExpressError,
  ]);

  return (
    <>
      <AmountInputComponent required={required}
                            decimals={decimalsConfig?.betSlip}
                            name={name}
                            disabled={disabled}
                            value={inputValue}
                            placeholder={placeholder}
                            title={placeholder}
                            {...dataGaAttributes}
                            onChange={changeInputHandler}
                            onFocus={focusInputHandler}
                            onBlur={blurInputHandler}
                            className={_className}
                            data-test={classNames(props['data-test'], 'amountInput-input')} />
      <div className={classNames(styles.units, unitsClassName, {
        [styles.withoutChainDropdown]: !isAllowChain,
      })}
           data-test="amountInput-unit">
        <CurrencySymbol currency={currency} />
      </div>
    </>
  );
};

const mapStateToProps = (state: ReduxState, { betId }: InputProps): StoreProps => {
  const hasEntriesErrors = selectSomeHasError(state) || selectAnyHasLockedState(state);
  const betsInfo = selectSingleBetsInfo(state);
  const betInfo = betId ? betsInfo.get(betId) : null;
  const price = betId && selectBets(state).get(betId)?.price;

  return {
    price,
    totalAmountActive: selectTotalAmountActive(state),
    required: actionButtonState(state) !== ActionButtonState.UNAUTHORIZED,
    currency: selectCurrency(state),
    disabled: selectSubmission(state) !== SubmissionState.AWAITING || hasEntriesErrors,
    isOnlyAcceptSingleError: betId ? selectIsOnlyAcceptAmountSingleError(state, betId) : false,
    isOnlyAcceptExpressError: Boolean(selectOnlyAcceptAmountExpressError(state)),
    singleRejectAmount: betInfo?.error?.rejectReason?.amount,
    expressRejectAmount: betsInfo.last()?.error?.rejectReason?.amount,
  };
};

export default connect(mapStateToProps)(AmountInput);
